import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { APP_PATHS } from '../routing/app-paths';
import { canAccess, isPageKey } from './page-access';
import { safeReturnUrl } from './return-url';
import { SessionStore } from './session.store';

/**
 * Sends signed-out reps to Login with a returnUrl, and reps without page access to no-access.
 * Never clears the session: the shell is the single redirect point, and a clear here would race it.
 */
export const authGuard: CanActivateFn & CanActivateChildFn = (route, state) => {
  const session = inject(SessionStore);
  const router = inject(Router);

  if (!session.isSignedIn()) {
    return router.createUrlTree(['/', APP_PATHS.login], {
      queryParams: {
        returnUrl: state.url,
        reason: session.hasToken() ? 'expired' : undefined,
      },
    });
  }

  const page: unknown = route.data['page'];

  if (page === undefined) {
    return true;
  }

  const role = session.role();

  // An unknown page key is a mistyped route: fail closed.
  return (role !== null && isPageKey(page) && canAccess(role, page)) ||
    router.createUrlTree(['/', APP_PATHS.noAccess]);
};

/** Keeps a signed-in rep off Login. */
export const signedOutGuard: CanActivateFn = (route) => {
  const session = inject(SessionStore);
  const router = inject(Router);

  if (!session.isSignedIn()) {
    return true;
  }

  return router.parseUrl(safeReturnUrl(route.queryParamMap.get('returnUrl')) ?? '/');
};
